import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import { useDialogDismiss } from './ui/useDialogDismiss';

interface ModalProps {
  isOpen: boolean;
  title?: string;
  /**
   * Omit for a dialog with no way out (D101.1: a question that must be answered): no X button,
   * and Escape and a backdrop click do nothing. Every other caller passes it.
   */
  onClose?: () => void;
  children: React.ReactNode;
  maxWidthClass?: string; // e.g. "max-w-3xl"
  /** Stretch the panel to the full available height — for data-dense modals
   *  whose content manages its own scrolling regions. */
  fullHeight?: boolean;
  /** Replaces the default padding/scroll classes on the content wrapper. */
  bodyClass?: string;
  /**
   * Edge to edge — no backdrop gutter, no width cap, no rounded corners.
   *
   * For a modal that is really a screen: the vendor-invoice review grid is
   * twenty-odd columns wide and was being read through a letterbox, with the
   * page it covers still visible around it. Overrides `maxWidthClass` and
   * implies `fullHeight`.
   */
  fullScreen?: boolean;
  /**
   * The overlay's stacking level, as a Tailwind class. Defaults to the house level,
   * `z-[70]`, which every modal in the app shares.
   *
   * The one caller that overrides it is the bug reporter's form: it can now be opened from
   * on top of another popup (ClickUp 14yj85ny3xf), and a form that opens BEHIND the popup
   * it is reporting is the same dead end in a different place.
   */
  overlayZClass?: string;
  /**
   * Draw as a PAGE, not a popup: the content fills the area to the right of the sidebar (the layout's
   * `#page-overlay-root`), with no backdrop, so the sidebar, and the cycle it shows for the open document,
   * stay on screen. It is left with its back arrow only (no Escape, no click outside), because it is where
   * the person is working. With no such host (a test, a standalone render) it renders in place.
   */
  asPage?: boolean;
}

/** The layout's content-area overlay host, where an `asPage` modal draws. */
export const PAGE_OVERLAY_ROOT_ID = 'page-overlay-root';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  maxWidthClass = 'max-w-3xl',
  fullHeight = false,
  bodyClass,
  fullScreen = false,
  overlayZClass = 'z-[70]',
  asPage = false,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape and a backdrop click close this modal only when it is the dialog on top.
  const dismiss = useDialogDismiss(isOpen && !asPage, onClose ?? (() => {}), !onClose);

  // Focus the panel when opened (basic accessibility, no breaking behavior)
  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      // A control inside that already took focus (an `autoFocus` search box) keeps it: moving focus to
      // the panel here would send the person's first keystrokes, and an Escape meant for an open list,
      // to the panel instead.
      if (panelRef.current?.contains(document.activeElement)) return;
      panelRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) return null;

  if (asPage) {
    const page = (
      <div
        role="dialog"
        aria-label={title || 'Page'}
        className="pointer-events-auto flex h-full min-h-0 w-full flex-col bg-white"
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-slate-200 px-4 py-3 sm:px-8">
          {onClose && (
            <button
              type="button"
              aria-label={title ? `Close ${title}` : 'Close'}
              title="Back to the list"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          <h1 className="min-w-0 truncate text-xl font-semibold leading-7 text-slate-900">{title || ''}</h1>
        </div>
        <div className={bodyClass || 'px-4 py-4 sm:px-6 sm:py-5 flex-1 min-h-0 overflow-y-auto'}>{children}</div>
      </div>
    );
    const host = document.getElementById(PAGE_OVERLAY_ROOT_ID);
    return host ? createPortal(page, host) : page;
  }

  const content = (
    <div
      {...dismiss}
      // A dialog, and announced as one: this had no role at all, so a screen
      // reader met the page behind it (docs/DESIGN.md).
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
      // The scrim is a flat slate wash and NOT a blur. Same reasoning as
      // ActionDialog's own comment: a modal's job is to sit above the screen it
      // came from, not to smear it, and a 2px blur erases the 1px rings the
      // review grid draws underneath.
      className={`fixed inset-0 ${overlayZClass} flex items-center justify-center bg-slate-900/40 modal-overlay-safe ${
        fullScreen ? 'p-0' : 'p-4 sm:p-6'
      }`}
    >
      <div
        className={`w-full max-h-full flex flex-col min-h-0 ${
          fullScreen ? 'h-full max-w-none' : `${fullHeight ? 'h-full' : ''} ${maxWidthClass}`
        }`}
      >
        <div
          ref={panelRef}
          tabIndex={-1}
          className={`bg-white shadow-2xl flex flex-col overflow-hidden
            ${fullScreen ? 'rounded-none flex-1 min-h-0' : `rounded-xl ${fullHeight ? 'flex-1 min-h-0' : 'modal-safe-max-h'}`}
            animate-[modalIn_0.16s_ease-out]`}
        >
          {/* HEADER */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
            <div className="text-base font-semibold leading-6 text-slate-900">
              {title || ''}
            </div>

            {onClose && (
            <button
              type="button"
              // Named after its own dialog: with two modals open, two buttons
              // both called "Close" could not be told apart (VI-6 retest).
              aria-label={title ? `Close ${title}` : 'Close'}
              onClick={onClose}
              className="h-8 w-8 inline-flex items-center justify-center rounded-md
                         text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
            >
              <span className="text-xl leading-none">×</span>
            </button>
            )}
          </div>

          {/* CONTENT */}
          <div className={bodyClass || 'px-4 py-4 sm:px-6 sm:py-5 flex-1 min-h-0 overflow-y-auto'}>{children}</div>
        </div>

        {/* Tailwind doesn't ship keyframes; define small keyframes via inline style */}
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: translateY(6px) scale(0.99); }
            to   { opacity: 1; transform: translateY(0)   scale(1); }
          }
        `}</style>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};
